import { Button, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

const emissionUnits = [
  { value: "kgch4/hr", label: "kg CH₄ / h" },
  { value: "tch4/hr", label: "tons CH₄ / h" }
];

const SourceLevelMeasurements = ({ sources, setSources, versionsCategories, ogmpCategories }) => {
  const assignedCategoryIds = versionsCategories?.map((vc) => vc.ogmpCategoryId) || [];

  // filter ogmpCategories to only include those that are assigned
  const assignedCategories = ogmpCategories?.filter((cat) => assignedCategoryIds?.includes(cat.id)) || [];

  // function to get parent category based on ogmpCategoryId
  const getParentCategory = (categoryId) => {
    if (!categoryId) return "";

    const category = ogmpCategories?.find((cat) => cat.id === categoryId);

    if (category && category.parentCategoryId) {
      const parentCategory = ogmpCategories?.find((cat) => cat.id === category.parentCategoryId);
      return parentCategory ? parentCategory.name : "";
    }

    return "";
  };

  const addSourceField = () => {
    // check if there are available categories to add
    const usedIds = sources?.map((source) => source.sourceCategoryId) || [];
    const availableCategories = assignedCategories.filter((cat) => !usedIds.includes(cat.id));

    if (availableCategories?.length === 0) {
      return;
    }

    const newId = sources?.length > 0 ? Math.max(...sources.map((s) => s.id)) + 1 : 1;
    setSources([
      ...sources,
      {
        id: newId ? newId : null,
        sourceCategoryId: null,
        versionOgmpCategoryId: null,
        emissions: "",
        emissionsUnit: sources[0].emissionsUnit,
        summary: ""
      }
    ]);
  };

  const deleteSourceField = (id) => {
    setSources(sources.filter((source) => source.id !== id));
  };

  const handleSourceChange = (id, field, value) => {
    if (field === "sourceCategoryId") {
      const usedVersionOgmpCategoryId = versionsCategories?.find((voc) => voc.ogmpCategoryId === value)?.id;
      setSources(
        sources.map((source) =>
          source.id === id ? { ...source, sourceCategoryId: value, versionOgmpCategoryId: usedVersionOgmpCategoryId } : source
        )
      );
    } else {
      setSources(sources.map((source) => (source.id === id ? { ...source, [field]: value } : source)));
    }
  };

  // check if source name is already selected in another source
  const isSourceNameSelected = (sourceCategoryId, currentSourceId) => {
    return sources.some((source) => source.sourceCategoryId === sourceCategoryId && source.id !== currentSourceId);
  };

  return (
    <div className="source-section mb-5 p-4 bg-light border rounded">
      <h5 className="text-white p-2" style={{ backgroundColor: "#243544" }}>
        Source Level Measurements
      </h5>

      {sources.map((source, index) => (
        <div key={source.id} className="p-3 mb-3 border rounded">
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Source Name</Form.Label>
                <Select
                  options={assignedCategories.map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                    isDisabled: isSourceNameSelected(cat.id, source.id)
                  }))}
                  value={
                    assignedCategories
                      .filter((cat) => cat.id === source.sourceCategoryId)
                      .map((cat) => ({ value: cat.id, label: cat.name }))[0]
                  }
                  onChange={(selected) => handleSourceChange(source.id, "sourceCategoryId", selected.value)}
                  placeholder="Select source"
                  isSearchable
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Parent Category</Form.Label>
                <Form.Control type="text" value={getParentCategory(source.sourceCategoryId)} readOnly disabled />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Emissions</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  placeholder="Enter emissions value"
                  value={source.emissions}
                  onChange={(e) => handleSourceChange(source.id, "emissions", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Emissions Unit</Form.Label>
                <Select
                  options={emissionUnits}
                  value={emissionUnits.find((option) => option.value === source.emissionsUnit)}
                  isDisabled={true}
                  onChange={(selected) => handleSourceChange(source.id, "emissionsUnit", selected.value)}
                  placeholder="Select unit"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Source Summary</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter summary"
                  value={source.summary}
                  onChange={(e) => handleSourceChange(source.id, "summary", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-end mt-2">
            <Button variant="outline-danger" onClick={() => deleteSourceField(source.id)} className="d-flex align-items-center">
              <FontAwesomeIcon icon={faTrash} className="me-2" /> Remove Source
            </Button>
          </div>
        </div>
      ))}

      <div className="d-flex justify-content-center mt-3">
        <Button variant="outline-primary" onClick={addSourceField} className="d-flex align-items-center">
          <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Source Measurement
        </Button>
      </div>
    </div>
  );
};

export { SourceLevelMeasurements };
